import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: 'What is Superbuy Spreadsheet?',
    answer: 'Superbuy Spreadsheet is a structured product discovery and shopping resource platform designed to help international shoppers find, compare, and organize products more efficiently. It provides curated collections, detailed guides, and comparison tools that streamline the entire shopping process from discovery to purchase.',
  },
  {
    question: 'Is Superbuy Spreadsheet free to use?',
    answer: 'Yes, browsing the Superbuy Spreadsheet resource pages and accessing product category information is completely free. You can explore all our curated collections, read guides, and use comparison tools without any cost or subscription required.',
  },
  {
    question: 'How does Superbuy Spreadsheet work?',
    answer: 'The Superbuy Spreadsheet approach organizes products into clear categories with detailed information. You simply browse a category that interests you, review the curated product listings, compare options using our structured format, and then proceed to make informed purchasing decisions on the main shopping platform.',
  },
  {
    question: 'Is Superbuy Spreadsheet safe to use?',
    answer: 'Absolutely. Our website uses HTTPS encryption for secure browsing, maintains a clean browsing environment without deceptive elements, and does not require any personal information to access our resources. We prioritize user safety and transparency in everything we do.',
  },
  {
    question: 'How often is Superbuy Spreadsheet updated?',
    answer: 'Our product collections and guides are updated regularly to reflect new releases, trending items, and the latest shopping information. We continuously monitor the product landscape to ensure our resources remain fresh, relevant, and valuable to our users.',
  },
  {
    question: 'Can I find specific products like shoes or clothing?',
    answer: 'Yes! The Superbuy Spreadsheet platform organizes products across numerous categories including shoes, hoodies and sweaters, t-shirts, jackets, pants and shorts, headwear, sets, underwear, jerseys, accessories, bags, belts, perfumes, and many other items. Simply select your desired category to browse relevant products.',
  },
  {
    question: 'Do I need to create an account to use Superbuy Spreadsheet?',
    answer: 'No account creation is necessary. You can freely browse all our resource pages, product categories, guides, and comparison content without signing up or providing any personal information.',
  },
  {
    question: 'What makes Superbuy Spreadsheet different from regular searching?',
    answer: 'Unlike traditional searching where you spend hours scrolling through unorganized listings, the Superbuy Spreadsheet approach provides pre-organized, curated collections with structured product information. This saves significant time, enables better comparison, and helps you discover items you might otherwise miss.',
  },
  {
    question: 'How can I save money using Superbuy Spreadsheet?',
    answer: 'By providing organized product comparisons and curated selections, the Superbuy Spreadsheet helps you identify the best value options more quickly. Our guides also include money-saving tips, shopping strategies, and insights that experienced buyers use to optimize their purchases.',
  },
  {
    question: 'What categories are available on Superbuy Spreadsheet?',
    answer: 'We cover a wide range of product categories including Shoes, Hoodies and Sweaters, T-Shirts, Jackets, Pants and Shorts, Headwear, Sets, Underwear, Jerseys, Accessories, Bags, Belts, Perfumes, and more. Each category contains curated product listings organized for easy browsing.',
  },
  {
    question: 'Is there a Superbuy Spreadsheet mobile app?',
    answer: 'Currently, the Superbuy Spreadsheet is accessible as a mobile-friendly website that works seamlessly across all devices including smartphones and tablets. You can access all features directly from your mobile browser without any downloads.',
  },
  {
    question: 'How do I compare products using Superbuy Spreadsheet?',
    answer: 'Our comparison system presents key product information in a structured format, allowing you to evaluate different items side by side. You can assess factors like quality indicators, pricing tiers, category relevance, and user-focused details to make informed decisions.',
  },
  {
    question: 'Can beginners use Superbuy Spreadsheet easily?',
    answer: 'Yes! The Superbuy Spreadsheet is designed specifically to be beginner-friendly. Our intuitive navigation, clear category labels, step-by-step guides, and straightforward comparison tools make it accessible even if you are completely new to international online shopping.',
  },
  {
    question: 'Where can I find the latest Superbuy Spreadsheet updates?',
    answer: 'The most current product collections and guides are always available on our homepage. We continuously refresh our content, so visiting superbuyspread.casa regularly ensures you access the newest finds and latest shopping resources.',
  },
  {
    question: 'Does Superbuy Spreadsheet ship products directly?',
    answer: 'Superbuy Spreadsheet is an informational resource platform. We do not ship products or process payments directly. All purchasing transactions are completed independently on the main shopping platform. Our role is to help you discover, compare, and make informed decisions before you buy.',
  },
  {
    question: 'How do I avoid fake websites claiming to be Superbuy Spreadsheet?',
    answer: 'Always ensure you are visiting the official domain: superbuyspread.casa. Be wary of similar-looking URLs or sites that request personal information or payment details. The official Superbuy Spreadsheet site never asks for payment or sensitive personal data to access its resources.',
  },
  {
    question: 'What is the best way to start using Superbuy Spreadsheet?',
    answer: 'The best way to start is by visiting our homepage and browsing the product category that interests you most. You can also read our complete guide for a comprehensive walkthrough, or simply use the category dropdown to jump directly into the product type you are looking for.',
  },
  {
    question: 'Can I suggest new categories or products for Superbuy Spreadsheet?',
    answer: 'We welcome feedback and suggestions. While we do not have a direct submission form on the site, our team continuously monitors shopping trends and user interests to expand and improve the product categories and resources available.',
  },
  {
    question: 'Is Superbuy Spreadsheet available outside the United States?',
    answer: 'Yes! The Superbuy Spreadsheet resource platform is accessible worldwide. Whether you are shopping from the USA, UK, Canada, Australia, Europe, or anywhere else, you can access our curated product collections and guides from any location with internet access.',
  },
  {
    question: 'How does Superbuy Spreadsheet help with quality checking?',
    answer: 'Our platform includes guides and tips on evaluating product quality before purchasing. While we provide informational resources about quality indicators, detailed product photos, and comparison criteria, the final quality assessment and purchasing decision remains with the individual shopper.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
            FAQ
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            Frequently Asked Questions
          </h2>
          <p className="text-foreground-600 text-base max-w-xl mx-auto">
            Everything you need to know about Superbuy Spreadsheet. Can not find your answer? Browse our complete guide for more in-depth information.
          </p>
        </div>

        <div className="space-y-2">
          {faqData.map((item, index) => (
            <div
              key={index}
              className="rounded-lg border border-background-200/70 bg-background-100 overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-50 transition-colors duration-150"
              >
                <h4 className="font-medium text-foreground-800 text-sm pr-4">{item.question}</h4>
                <i
                  className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 flex-shrink-0 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {openIndex === index && (
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}